import React from 'react';
import SvgIcon from 'src/react_components/svg-icon';

interface MultiParagraphDisplayProps {
  sentences: string[];
  paragraphIndices?: number[];
  onParagraphClick: (paragraphIndex: number) => void;
  selectedParagraphIndex?: string | null;
}

/**
 * Displays a list of paragraphs that are part of a multi-paragraph annotation.
 * Each paragraph is clickable and will scroll to and highlight the corresponding
 * paragraph in the document.
 */
const MultiParagraphDisplay: React.FC<MultiParagraphDisplayProps> = ({
  sentences,
  paragraphIndices,
  onParagraphClick,
  selectedParagraphIndex
}) => {
  if (!sentences || sentences.length === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {sentences.map((text, index) => {
        const paragraphIndex = paragraphIndices?.[index];

        // Check if this paragraph is selected
        const isSelected = selectedParagraphIndex !== null &&
          selectedParagraphIndex !== undefined &&
          paragraphIndex !== undefined &&
          selectedParagraphIndex.toString() === paragraphIndex.toString();

        // Generate accessible label with fallback
        const ariaLabel = paragraphIndex !== undefined
          ? `Jump to paragraph ${paragraphIndex + 1}`
          : 'Jump to paragraph';

        return (
          <div
            key={paragraphIndex != null ? `para-${paragraphIndex}` : `para-${index}`}
            className="multi-paragraph-item d-flex align-items-start gap-2 clickable"
            onClick={(e) => {
              e.stopPropagation();
              if (paragraphIndex !== undefined) {
                onParagraphClick(paragraphIndex);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (paragraphIndex !== undefined) {
                  onParagraphClick(paragraphIndex);
                }
              }
            }}
          >
            <div className={`multi-paragraph-icon flex-shrink-0 ${isSelected ? 'selected' : ''}`}>
              <SvgIcon icon="comment-category-task-and-content" />
            </div>
            <div className="multi-paragraph-text-box">
              {text}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MultiParagraphDisplay;
